import { Maybe, OfferDto, Scalars } from '@shared/loom-api/.generated/types'

/**
 * Converts a UTC datetime string to New Zealand timezone
 */
function convertUTCToNZTimezone(utcDateTime: string): string {
  const date = new Date(utcDateTime)
  return date.toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

/**
 * Converts a nullable UTC datetime string to New Zealand timezone
 */
function convertNullableUTCToNZTimezone(utcDateTime?: Maybe<string>): Maybe<string> {
  if (!utcDateTime) return null
  return convertUTCToNZTimezone(utcDateTime)
}

/**
 * Converts a date string to dd/mm/yyyy format
 */
function convertToNZDateFormat(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export type FlattenedOfferDto = {
  'Completed Date Time'?: Maybe<Scalars['DateTime']['output']>
  Created: Scalars['DateTime']['output']
  'Created By'?: Maybe<string> | undefined
  'Expiry Date Time': Scalars['DateTime']['output']
  'Offer From': Scalars['String']['output']
  'Payment Date': Scalars['LocalDate']['output']
  'Personalised Comment'?: Maybe<string> | undefined
  'Responded By'?: Maybe<string> | undefined
  'Station Brand'?: Maybe<string> | undefined
  'Station Record Number'?: Maybe<string> | undefined
  'Offer Status': string
  'Submitted On Behalf Of Grower'?: string | undefined

  /** The brand partner for which the price offer is destined, displayed on the offer alongside the price */
  'Brand Partner'?: Maybe<string> | undefined
  /** The clean market price for the WoolLot - units of cents per kg */
  'Clean Market Price'?: Maybe<Scalars['Int']['output']>
  /** The clean price offered for the WoolLot - units of cents per kg */
  'Clean Offer Price'?: Maybe<Scalars['Int']['output']>
  Code?: Maybe<string> | undefined
  'Colour YZ'?: Maybe<Scalars['Decimal']['output']>
  Micron?: Maybe<Scalars['Decimal']['output']>
  'Staple Length Greasy'?: Maybe<Scalars['Decimal']['output']>
  'Staple Strength'?: Maybe<Scalars['Decimal']['output']>
  'Vegetable Matter Base'?: Maybe<Scalars['Decimal']['output']>
  Yield?: Maybe<Scalars['Decimal']['output']>
  /**
   * The setting controlling if value add should be displayed on the offer
   * Normally, NZM staff choose not to display value add on the offer if it is a very small benefit to the grower
   */
  'Display Value Add'?: string | undefined
  /** The greasy market price for the WoolLot - units of cents per kg */
  'Greasy Market Price'?: Maybe<Scalars['Int']['output']>
  /** The greasy price offered for the WoolLot - units of cents per kg */
  'Greasy Offer Price'?: Maybe<Scalars['Int']['output']>
  'Interlot Warning': string
  'Internal Comment'?: Maybe<string> | undefined
  'Internal Reference Code': Scalars['String']['output']
  'Line Number'?: Maybe<string> | undefined
  'Main Bale Header Purchase Description'?: Maybe<string> | undefined
  'Net Kg'?: Maybe<Scalars['Decimal']['output']>
  'Purchase Account Name'?: Maybe<string> | undefined
  'Quality Scheme 2'?: Maybe<string> | undefined
  'Quality Scheme Warning': string
  'Sale Number'?: Maybe<string> | undefined
  Season?: Maybe<string> | undefined
  /** The status of the offers made on the lot */
  'Wool Lot Offer Status'?: Maybe<Scalars['String']['output']>
  'Total Bales'?: Maybe<Scalars['Decimal']['output']>
  'Type Current'?: Maybe<string> | undefined
  /**
   * The value add of the price offer - units of dollars for entire lot
   * This represents benefit to the grower if they accept the offer
   * This is calculated client-side (in the Hub front-end)
   */
  'Value Add'?: Maybe<Scalars['Decimal']['output']>
}

export function flattenOffers(offers: OfferDto[]): FlattenedOfferDto[] {
  const flattenedOffers: FlattenedOfferDto[] = []

  for (const offer of offers) {
    for (const line of offer.lines) {
      const flattenedOffer: FlattenedOfferDto = {
        // Offer-level properties
        'Completed Date Time': convertNullableUTCToNZTimezone(offer.completedDateTime),
        Created: convertUTCToNZTimezone(offer.created),
        'Created By': offer.createdBy,
        'Expiry Date Time': convertUTCToNZTimezone(offer.expiryDateTime),
        'Offer From': offer.offerFrom,
        'Payment Date': convertToNZDateFormat(offer.paymentDate),
        'Personalised Comment': offer.personalisedComment,
        'Responded By': offer.respondedBy,
        'Station Brand': offer.stationBrand,
        'Station Record Number': offer.stationRecordNumber,
        'Offer Status': offer.status,
        'Submitted On Behalf Of Grower': offer.submittedOnBehalfOfGrower?.toString() ?? undefined,

        // Line-level properties
        'Brand Partner': line.brandPartner,
        'Clean Market Price': line.cleanMarketPrice,
        'Clean Offer Price': line.cleanOfferPrice,
        Code: line.code,
        'Colour YZ': line.coreTestColourYZ,
        Micron: line.coreTestMicron,
        'Staple Length Greasy': line.coreTestStapleLengthGreasy,
        'Staple Strength': line.coreTestStapleStrength,
        'Vegetable Matter Base': line.coreTestVegetableMatterBase,
        Yield: line.coreTestYield,
        'Display Value Add': line.displayValueAdd?.toString() ?? undefined,
        'Greasy Market Price': line.greasyMarketPrice,
        'Greasy Offer Price': line.greasyOfferPrice,
        'Interlot Warning': line.interlotWarning.toString(),
        'Internal Comment': line.internalComment,
        'Internal Reference Code': line.internalReferenceCode,
        'Line Number': line.lineNumber,
        'Main Bale Header Purchase Description': line.mainBaleHeaderPurchaseDescription,
        'Net Kg': line.netKg,
        'Purchase Account Name': line.purchaseAccountName,
        'Quality Scheme 2': line.qualityScheme2,
        'Quality Scheme Warning': line.qualitySchemeWarning.toString(),
        'Sale Number': line.saleNumber,
        Season: line.season,
        'Wool Lot Offer Status': line.status,
        'Total Bales': line.totalBales,
        'Type Current': line.typeCurrent,
        'Value Add': line.valueAdd,
      }

      flattenedOffers.push(flattenedOffer)
    }
  }

  return flattenedOffers
}
